import { ComponentClass } from 'react';
import { createRoot, Root } from 'react-dom/client';
import { EXPORT_CONTAINER_ID } from '../../common/higher-order-components/with-shared-providers';
import { InstanceExportOptions } from '../options/InstanceExportOptions';
import { OrganizationExportOptions } from '../options/OrganizationExportOptions';
import { ProjectExportOptions } from '../options/ProjectExportOptions';
import { ReportExportOptions } from '../options/ReportExportOptions';
import { BasePrintControllerProps } from '../printable/common/controllers/BasePrintController';
import { InstancePrintController } from '../printable/instance/controllers/InstancePrintController';
import { OrganizationPrintController } from '../printable/organization/controllers/OrganizationPrintController';
import { ProjectPrintController } from '../printable/project/controllers/ProjectPrintController';
import { ReportPrintController } from '../printable/report/controllers/ReportPrintController';
import { ReactPrintWrapper } from '../printable/wrappers/ReactPrintWrapper';
import { formatExportName } from '../utils/exportNameUtils';

export class PrintExportManager {
  private reactRoot: Root | null = null;

  /**
   * Displays the print dialog to export a filtered view of the organization dashboard
   * @param organizationName the name of the organization, used to set a default file name
   * @param options options to control the content of the document
   */
  exportOrganizationDashboard(organizationName: string, options: OrganizationExportOptions) {
    const exportName = formatExportName(organizationName, { extension: 'pdf' });
    return this.print(OrganizationPrintController, { options }, exportName);
  }

  /**
   * Displays the print dialog to export the summary of a report
   * @param reportId the ID of the report to be printed
   * @param reportName the name of the report, used to set a default file name
   * @param options options to control the content of the document
   */
  exportReportSummary(reportId: number, reportName: string, options: ReportExportOptions) {
    const exportName = formatExportName(reportName, { extension: 'pdf', exportType: 'summary' });
    return this.print(ReportPrintController, { reportId, options }, exportName);
  }

  /**
   * Displays the print dialog to export the summary of a project
   * @param projectId the ID of the project to be printed
   * @param projectName the name of the project, used to set a default file name
   * @param options options to control the content of the document
   */
  exportProjectDashboard(projectId: number, projectName: string, options: ProjectExportOptions) {
    const exportName = formatExportName(projectName, { extension: 'pdf', exportType: 'summary' });
    return this.print(ProjectPrintController, { projectId, options }, exportName);
  }

  /**
   * Displays the print dialog to export the summary of an instance
   * @param instanceName used as default file name
   * @param options options to control the content of the document
   */
  exportInstance(instanceName: string, options: InstanceExportOptions) {
    const exportName = formatExportName(instanceName, { extension: 'pdf', exportType: 'summary' });
    return this.print(InstancePrintController, { options }, exportName);
  }

  private print<T extends BasePrintControllerProps>(
    Component: ComponentClass<T>,
    props: Omit<T, keyof BasePrintControllerProps>,
    printTitle: string
  ): Promise<void> {
    const root = this.getRoot();
    return new Promise((resolve, reject) => {
      const onDone = () => {
        this.unmount();
        resolve();
      };
      const onError = (e: unknown) => {
        this.unmount();
        reject(e);
      };

      root.render(<ReactPrintWrapper Component={Component} componentProps={props} printTitle={printTitle} onDone={onDone} onError={onError} />);
    });
  }

  private getRoot() {
    if (this.reactRoot === null) {
      const container = document.getElementById(EXPORT_CONTAINER_ID)!;
      this.reactRoot = createRoot(container);
    }
    return this.reactRoot;
  }

  private unmount() {
    if (this.reactRoot !== null) {
      this.reactRoot.unmount();
      this.reactRoot = null;
    }
  }
}
